import { isDefined } from "@clipboard-health/util-ts";
import {
  type CancelShiftPolicy,
  type EmergencyCreditStatsCommon,
  useGetClipboardScoreProfile,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetShiftDetailsData } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/api/useGetShiftDetailsData";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

export interface UseCancelShiftPageDataParams {
  shiftId: string;
  currentDate: Date;
}

export interface CancelShiftPageDataLoading {
  isLoading: true;
}
export interface CancelShiftPageDataErrored {
  isLoading: false;
  isError: true;
}
export interface CancelShiftPageDataSuccessful {
  isLoading: false;
  isError: false;
  cancellationPolicy: CancelShiftPolicy | undefined;
  currentScore: number;
  scoreChange: number;
  emergencyCancellationCreditStats: EmergencyCreditStatsCommon | undefined;
  hasCancellationCredit: boolean;
  canUseCredit: boolean;
}
export type CancelShiftPageData =
  | CancelShiftPageDataLoading
  | CancelShiftPageDataErrored
  | CancelShiftPageDataSuccessful;

function findCancellationPolicy(
  policies: CancelShiftPolicy[] | undefined,
  leadTimeHours: number
): CancelShiftPolicy | undefined {
  if (!isDefined(policies)) {
    return undefined;
  }

  return policies.find((policy) => {
    const [minHours, maxHours] = policy.leadTime;
    const meetsMinimum = !isDefined(minHours) || leadTimeHours >= minHours;
    const meetsMaximum = !isDefined(maxHours) || leadTimeHours < maxHours;
    return meetsMinimum && meetsMaximum;
  });
}

export function useCancelShiftPageData(params: UseCancelShiftPageDataParams): CancelShiftPageData {
  const { shiftId, currentDate } = params;

  const { userId } = useDefinedWorker();

  const { shift, isLoading: isShiftLoading, shiftError } = useGetShiftDetailsData({ shiftId });

  const {
    data: clipboardScoreProfile,
    isLoading: isClipboardScoreLoading,
    isError: isClipboardScoreError,
  } = useGetClipboardScoreProfile(
    userId,
    { enabled: isDefined(userId) },
    shift?.agentReq,
    shift?.facilityId
  );

  const isLoading = isShiftLoading || isClipboardScoreLoading;
  const isError = isDefined(shiftError) || isClipboardScoreError;

  // The data doesn't change after loading, so the whole thing can be memoized.
  return useMemo((): CancelShiftPageData => {
    if (isError) {
      return { isLoading: false, isError };
    }
    if (isLoading) {
      return { isLoading };
    }

    // If the shift isn't defined after isLoading is false, something went wrong.
    if (!isDefined(shift?.start)) {
      return { isError: true, isLoading: false };
    }

    // Calculate lead time from shift start. This *must* match the backend calculation.
    const startDate = parseISO(shift.start);
    const leadTimeHours = differenceInMinutes(startDate, currentDate) / 60;

    const cancellationPolicy = findCancellationPolicy(
      clipboardScoreProfile?.policy?.cancelShift,
      leadTimeHours
    );

    const currentScore = clipboardScoreProfile?.clipboardScore ?? 0;
    const scoreChange = cancellationPolicy?.points ?? 0;
    const emergencyCancellationCreditStats =
      clipboardScoreProfile?.emergencyCreditStats?.cancellation;
    const hasCancellationCredit =
      (emergencyCancellationCreditStats?.totalAvailableCredits ?? 0) > 0;
    const canUseCredit = hasCancellationCredit && leadTimeHours < 24;

    return {
      isLoading,
      isError,
      cancellationPolicy,
      currentScore,
      scoreChange,
      emergencyCancellationCreditStats,
      hasCancellationCredit,
      canUseCredit,
    };
  }, [isLoading, isError, shift?.start, currentDate, clipboardScoreProfile]);
}
